import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './index.css'
import { validateEnv, ENV } from './config/env'
import ErrorBoundary from './components/ErrorBoundary'
import * as Sentry from '@sentry/react'
import { initializeAnalytics } from './lib/analytics'

// Initialize Sentry if DSN is provided
if (ENV.SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: ENV.SENTRY_DSN,
      environment: ENV.SENTRY_ENVIRONMENT || import.meta.env.MODE,
      release: ENV.SENTRY_RELEASE || `so-lookup-2025@${import.meta.env.MODE}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      // Disable replays temporarily to avoid quota limits
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.0,
      beforeSend: (event) => {
        // Don't send events in development unless explicitly enabled
        if (import.meta.env.DEV && !ENV.SENTRY_ENVIRONMENT) {
          console.log('Sentry event blocked in development:', event);
          return null;
        }
        return event;
      },
    });
  } catch (error) {
    console.warn('Failed to initialize Sentry:', error);
  }
}

// Initialize Google Analytics 4 if measurement ID is provided
if (ENV.GA4_MEASUREMENT_ID) {
  initializeAnalytics();
}

// Validate environment variables before rendering
try {
  validateEnv();
  
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </React.StrictMode>,
  );
} catch (error) {
  console.error('Application failed to start:', error.message);
  
  // Show a user-friendly error message
  ReactDOM.createRoot(document.getElementById('root')).render(
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Configuration Error
          </h2>
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Missing Environment Variables
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    The application is missing required configuration. Please ensure all environment variables are set in Cloudflare Pages:
                  </p>
                  <pre className="mt-2 text-xs bg-red-100 p-2 rounded">
                    {error.message}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}